import {useCallback, useEffect} from 'react';
import {usePaymentsStore} from '../store/paymentsStore';
import {usePaymentsUIStore} from '../store/paymentsUIStore';
import {PaymentFilterParams} from '../models/Payment';
import {PAGINATION_CONFIG} from '../../../constants/appConfig';

/**
 * Unified hook for payments management that combines data and UI state
 * This replaces the need for multiple useState calls in components
 */
export const usePaymentsManagement = () => {
  // Data stores
  const paymentsStore = usePaymentsStore();
  const uiStore = usePaymentsUIStore();

  // Destructure from paymentsStore
  const {
    payments, statistics, paymentsPagination, loading, error,
    fetchPayments, fetchPaymentsWithFilters, fetchStatistics,
    refreshData, clearErrors
  } = paymentsStore;

  // Destructure from uiStore
  const {
    activeTab, setActiveTab,
    filters, setFilters, clearFilters, showFilters, setShowFilters,
    transferNotes, updateTransferNote,
    confirmDialog, openConfirmDialog, closeConfirmDialog,
    reset: resetUI
  } = uiStore;

  // Unified Actions
  const handleTabChange = useCallback((tab: 'payments' | 'transfers' | 'statistics') => {
    setActiveTab(tab);
  }, [setActiveTab]);

  const handleFilterChange = useCallback((newFilters: PaymentFilterParams) => {
    setFilters(newFilters);
    // Reset to first page when filters change
    fetchPaymentsWithFilters({
      ...newFilters,
      page: 1,
      limit: PAGINATION_CONFIG.DEFAULT_PAGE_SIZE
    });
  }, [setFilters, fetchPaymentsWithFilters]);

  const handleClearFilters = useCallback(() => {
    clearFilters();
    fetchPaymentsWithFilters({
      page: 1,
      limit: PAGINATION_CONFIG.DEFAULT_PAGE_SIZE
    });
  }, [clearFilters, fetchPaymentsWithFilters]);

  const handlePageChange = useCallback((newPage: number) => {
    const currentLimit = paymentsPagination?.limit || PAGINATION_CONFIG.DEFAULT_PAGE_SIZE;
    fetchPaymentsWithFilters({
      ...filters,
      page: newPage,
      limit: currentLimit
    });
  }, [fetchPaymentsWithFilters, filters, paymentsPagination?.limit]);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    fetchPaymentsWithFilters({
      ...filters,
      page: 1,
      limit: newPageSize
    });
  }, [fetchPaymentsWithFilters, filters]);

  const handleTransferNoteChange = useCallback((invoiceId: string, note: string) => {
    updateTransferNote(invoiceId, note);
  }, [updateTransferNote]);

  const handleOpenConfirmDialog = useCallback((type: 'approve' | 'reject', invoiceId: number | string, invoiceNumber: string) => {
    openConfirmDialog(type, invoiceId, invoiceNumber);
  }, [openConfirmDialog]);

  const handleCloseConfirmDialog = useCallback(() => {
    closeConfirmDialog();
  }, [closeConfirmDialog]);

  // Auto-fetch data when tab changes
  useEffect(() => {
    if (activeTab === 'payments') {
      fetchPaymentsWithFilters({
        ...filters,
        page: 1,
        limit: PAGINATION_CONFIG.DEFAULT_PAGE_SIZE
      });
    } else if (activeTab === 'statistics') {
      fetchStatistics();
    }
  }, [activeTab, fetchPaymentsWithFilters, fetchStatistics, filters]);

  // Reset all stores
  const resetAllStores = useCallback(() => {
    clearErrors();
    resetUI();
  }, [clearErrors, resetUI]);

  return {
    // UI State
    activeTab, setActiveTab,
    filters, setFilters, clearFilters, showFilters, setShowFilters,
    transferNotes, updateTransferNote: handleTransferNoteChange,
    confirmDialog, openConfirmDialog: handleOpenConfirmDialog, closeConfirmDialog: handleCloseConfirmDialog,

    // Data
    payments, statistics, paymentsPagination,

    // Loading States
    loading,

    // Error States
    error,

    // Actions
    handleTabChange, handleFilterChange, handleClearFilters,
    handlePageChange, handlePageSizeChange,
    fetchPayments, fetchPaymentsWithFilters, fetchStatistics,
    refreshData, resetAllStores
  };
};
